import ct from 'countries-and-timezones';
import { calculateAvailabilityAndLeadTime } from './helpers/availability-lead-time';

function isSupportedTimeZone(timezone) {
  try {
    Intl.DateTimeFormat('en', { timeZone: timezone }).format(0);

    return true;
  } catch {
    return false;
  }
}

// Gregorian calendar and Latin digits, so the numeric parts below always parse.
const numericLocale = 'en-u-ca-gregory-nu-latn';

// Reads one numeric field out of Intl formatToParts output. Part types are locale-independent,
// unlike a formatted date string, whose field order, separators and digits vary by locale.
function getNumericPart(parts, type) {
  return Number(parts.find((part) => part.type === type)?.value);
}

class ProductShippingMethod extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();

    this.containerElement = this.querySelector('.product-shipping-rates');
    this.shippingRateData = JSON.parse(this.containerElement.dataset.options);
    this.shippingToWrapper = this.querySelector('[data-shipping-to]');
    this.shipmentIsFreeWrapper = this.querySelector('[data-shipment-is-free]');
    this.expectedDeliveryIn = document.querySelector('[data-expected-delivery-in]');

    this.renderExpectedDeliveryIn(1);
    this.showShippingPrice(1);

    this.quantityInput = document.querySelector('product-form .quantity input');

    this.quantityInput.addEventListener('change', this.onQuantityInputChange.bind(this), {
      signal: this.abortController.signal,
    });

    document
      .getElementById('cart-icon-bubble')
      .addEventListener('cart-update', this.onCartUpdated.bind(this), { signal: this.abortController.signal });
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  /**
   * Handle cart updated events
   * @param {CustomEvent} event
   * @returns
   */
  onCartUpdated(event) {
    const { cartTotalExclVAT, productQuantity } = event.detail;

    this.shippingRateData.cartValue = cartTotalExclVAT;
    this.shippingRateData.quantityAlreadyInCart = productQuantity;

    const quantityInputValue = this.quantityInput.value;

    this.renderExpectedDeliveryIn(quantityInputValue);
    this.showShippingPrice(quantityInputValue);
  }

  onQuantityInputChange(event) {
    const currentInputValue = parseInt(event.target.value, 10);

    this.renderExpectedDeliveryIn(currentInputValue);
    this.showShippingPrice(currentInputValue);
  }

  // Returns the cutoff hour and timezone abbreviation in the selected country's first supported timezone.
  // Converts the NL cutoff hour to UTC (accounting for CET/CEST DST),
  // then re-formats in that timezone, falling back to the NL timezone if needed.
  getLocalCutoffInfo() {
    const { cutoffHour } = this.shippingRateData;
    const nlTimezone = 'Europe/Amsterdam';
    const countryData = ct.getCountry(this.shippingRateData.countryCode);
    const supportedTimezones = (countryData?.timezones ?? []).filter(isSupportedTimeZone);
    const targetTimezone = supportedTimezones[0] ?? nlTimezone;

    // The cutoff hour is a number setting, so anything else means the data is unusable.
    if (typeof cutoffHour !== 'number' || !Number.isFinite(cutoffHour)) {
      return null;
    }

    // Today's date as seen in NL. Read through formatToParts rather than splitting a formatted
    // string: that assumed an ISO layout, and locales that resolve to another date format produced
    // a NaN date which then threw out of formatToParts below.
    const now = new Date();
    const nlDateParts = new Intl.DateTimeFormat(numericLocale, {
      timeZone: nlTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);

    const year = getNumericPart(nlDateParts, 'year');
    const month = getNumericPart(nlDateParts, 'month');
    const day = getNumericPart(nlDateParts, 'day');

    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return null;
    }

    // Find NL's current UTC offset by checking what NL hour noon-UTC maps to.
    // Using noon avoids any DST-transition edge cases at midnight.
    const noonUTC = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    const nlHourAtNoon = getNumericPart(
      new Intl.DateTimeFormat(numericLocale, {
        timeZone: nlTimezone,
        hour: '2-digit',
        hour12: false,
      }).formatToParts(noonUTC),
      'hour'
    );

    if (!Number.isFinite(nlHourAtNoon)) {
      return null;
    }

    const nlUTCOffset = (nlHourAtNoon % 24) - 12; // +1 in winter (CET), +2 in summer (CEST)

    // Build the UTC moment that equals cutoffHour:00 in NL time
    const cutoffUTCHour = cutoffHour - nlUTCOffset;
    const cutoffDate = new Date(Date.UTC(year, month - 1, day, cutoffUTCHour, 0, 0));

    const parts = new Intl.DateTimeFormat(numericLocale, {
      timeZone: targetTimezone,
      hour: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    }).formatToParts(cutoffDate);

    const hour = getNumericPart(parts, 'hour');
    const timezone = parts.find((part) => part.type === 'timeZoneName')?.value;

    if (!Number.isFinite(hour)) {
      return null;
    }

    return { hour: hour % 24, timezone };
  }

  isSkipCutoffCountry() {
    const skipList = this.shippingRateData.cutoffSkipCountries
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
    return skipList.includes(this.shippingRateData.countryCode);
  }

  renderExpectedDeliveryIn(currentInputValue) {
    const { availabilityStatus, leadTime } = calculateAvailabilityAndLeadTime(
      this.shippingRateData.leadTimeArray,
      this.shippingRateData.inventoryQuantity,
      this.shippingRateData.inventoryPolicy,
      currentInputValue + this.shippingRateData.quantityAlreadyInCart,
      this.shippingRateData.useUp
    );

    const totalMinDays = this.shippingRateData.mintransitTime + leadTime;
    const totalMaxDays = this.shippingRateData.maxtransitTime + leadTime;

    let deliveryText;
    if (availabilityStatus === 'unavailable' || availabilityStatus === 'request') {
      deliveryText = '';
    } else {
      // A null cutoff (unparseable locale data) falls through to the plain delivery estimate
      // below, keeping the shipping block visible instead of throwing out of connectedCallback.
      const { hour: localCutoff, timezone: localTimezone } = this.getLocalCutoffInfo() ?? {};
      // Only show the cutoff prefix when: delivery is fast, country not manually skipped,
      // and the local cutoff time falls within a sensible window (avoids showing e.g. "Order before 1h")
      const showCutoff = totalMaxDays <= 4 && !this.isSkipCutoffCountry() && localCutoff >= 8 && localCutoff <= 22;

      const countryData = ct.getCountry(this.shippingRateData.countryCode);
      const offsets = (countryData?.timezones ?? [])
        .filter(isSupportedTimeZone)
        .map((tz) => ct.getTimezone(tz)?.utcOffset);
      const hasMultipleTimezones = new Set(offsets).size > 1;
      const applyTimezone = (str) =>
        str.replace('{{ timezone }}', hasMultipleTimezones && localTimezone ? ` (${localTimezone})` : '');

      if (showCutoff) {
        if (totalMinDays !== totalMaxDays) {
          deliveryText = applyTimezone(
            this.shippingRateData.orderBeforeDeliveryIn
              .replace('{{ cutoff_time }}', localCutoff)
              .replace('{{ min_transit_time }}', totalMinDays)
              .replace('{{ max_transit_time }}', totalMaxDays)
          );
        } else if (totalMaxDays === 1) {
          deliveryText = applyTimezone(
            this.shippingRateData.orderBeforeDeliveryInOne.replace('{{ cutoff_time }}', localCutoff)
          );
        } else {
          deliveryText = applyTimezone(
            this.shippingRateData.orderBeforeDeliveryInDay
              .replace('{{ cutoff_time }}', localCutoff)
              .replace('{{ min_transit_time }}', totalMinDays)
          );
        }
      } else if (this.shippingRateData.mintransitTime !== this.shippingRateData.maxtransitTime) {
        deliveryText = this.shippingRateData.expectedDeliveryIn
          .replace('{{ min_transit_time }}', totalMinDays)
          .replace('{{ max_transit_time }}', totalMaxDays);
      } else if (leadTime === 0) {
        deliveryText = this.shippingRateData.expectedDeliveryInOne;
      } else {
        deliveryText = this.shippingRateData.expectedDeliveryInDay.replace('{{ min_transit_time }}', totalMinDays);
      }
    }

    if (this.expectedDeliveryIn) {
      this.expectedDeliveryIn.innerHTML = deliveryText;
    }
  }

  showShippingPrice(currentInputValue) {
    const { cartValue, productPrice, freeShipment } = this.shippingRateData;
    const newCartPrice = cartValue + currentInputValue * productPrice;
    const freeShipmentReached = freeShipment && newCartPrice > freeShipment;

    // If free shipment is reached, let's hide shipping cost
    this.shippingToWrapper.hidden = freeShipmentReached;
    // And replace with a text to indicate free shipping is reached
    this.shipmentIsFreeWrapper.hidden = !freeShipmentReached;
  }
}

if (!customElements.get('product-shipping-method')) {
  customElements.define('product-shipping-method', ProductShippingMethod);
}
